import { zodResolver } from "@hookform/resolvers/zod"
import { useAtomValue } from "jotai"
import { useEffect } from "react"
import { ArrowLeft } from "react-feather"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import * as z from "zod"
import { authApi } from "@/api/client/auth"
import { useSearchAddressByPostalCode } from "@/api/client/postal-code"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import { cn } from "@/lib/utils"
import { sessionUserAtom, useSessionRefetch } from "@/store/session"

const profileFormSchema = z.object({
  name: z.string().min(1, "氏名を入力してください"),
  nameKana: z.string().min(1, "かなを入力してください"),
  phone: z
    .string()
    .min(1, "電話番号を入力してください")
    .regex(/^\d{10,11}$/, "電話番号は10〜11桁の数字で入力してください"),
  emergencyPhone: z
    .string()
    .regex(/^\d{10,11}$/, "緊急用電話番号は10〜11桁の数字で入力してください")
    .optional()
    .or(z.literal("")),
  postalCode: z.string().regex(/^\d{7}$/, "郵便番号は7桁の数字で入力してください"),
  address: z.string().min(1, "住所を入力してください"),
})

type ProfileFormValues = z.infer<typeof profileFormSchema>

type TabItem = {
  label: string
  path: string
  isActive: boolean
}

function ProfileEditTabs({ tabs }: { tabs: TabItem[] }) {
  return (
    <div className="bg-slate-100 rounded-md p-1 flex">
      {tabs.map((tab) => (
        <Link
          key={tab.path}
          to={tab.path}
          className={cn(
            "flex-1 text-center py-1.5 px-3 text-sm font-semibold rounded-sm transition-all",
            tab.isActive ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-700"
          )}
        >
          {tab.label}
        </Link>
      ))}
    </div>
  )
}

export function EditProfilePage() {
  const navigate = useNavigate()
  const sessionUser = useAtomValue(sessionUserAtom)
  const refetchSession = useSessionRefetch()

  const { mutateAsync: updateProfile, isPending } = authApi.useUpdateProfile({
    onSuccess: async () => {
      await refetchSession()
      toast.success("プロフィールを更新しました")
      navigate("/profile")
    },
    onError: () => {
      toast.error("プロフィールの更新に失敗しました")
    },
  })

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: "",
      nameKana: "",
      phone: "",
      emergencyPhone: "",
      postalCode: "",
      address: "",
    },
  })

  // セッションユーザー情報をフォームの初期値として設定
  useEffect(() => {
    if (sessionUser) {
      form.reset({
        name: sessionUser.name ?? "",
        nameKana: (sessionUser.lastNameKana as string) ?? "",
        phone: (sessionUser.phoneNumber as string) ?? "",
        emergencyPhone: (sessionUser.emergencyPhoneNumber as string) ?? "",
        postalCode: (sessionUser.postalCode as string) ?? "",
        address: (sessionUser.address as string) ?? "",
      })
    }
  }, [sessionUser, form])

  const { mutateAsync: searchAddress, isPending: isSearchingAddress } =
    useSearchAddressByPostalCode({
      onSuccess: (result) => {
        if (result) {
          form.setValue("address", result.fullAddress)
        } else {
          form.setError("postalCode", {
            message: "該当する住所が見つかりませんでした",
          })
        }
      },
      onError: () => {
        form.setError("postalCode", { message: "住所の検索に失敗しました" })
      },
    })

  const tabs: TabItem[] = [
    { label: "基本情報", path: "/profile/edit", isActive: true },
    { label: "メールアドレス", path: "/profile/edit/change-email", isActive: false },
    { label: "パスワード", path: "/profile/edit/change-password", isActive: false },
  ]

  const handleAddressSearch = async () => {
    const postalCode = form.getValues("postalCode")
    if (postalCode.length !== 7) {
      form.setError("postalCode", {
        message: "郵便番号は7桁の数字で入力してください",
      })
      return
    }

    await searchAddress(postalCode)
  }

  const handleSubmit = async (values: ProfileFormValues) => {
    await updateProfile(values)
  }

  const handleCancel = () => {
    navigate("/profile")
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <Button variant="outline" size="sm" onClick={() => navigate("/profile")}>
          <ArrowLeft className="size-4" />
          戻る
        </Button>
        <h1 className="text-base font-semibold text-primary">プロフィール設定</h1>
      </div>

      {/* Tabs */}
      <ProfileEditTabs tabs={tabs} />

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          <Card>
            <CardContent className="pt-5 space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-600">氏名</FormLabel>
                    <FormControl>
                      <Input placeholder="山田 太郎" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nameKana"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-600">かな</FormLabel>
                    <FormControl>
                      <Input placeholder="やまだ たろう" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-600">電話番号</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="01234567891"
                        maxLength={11}
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "")
                          field.onChange(value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emergencyPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-600">
                      緊急用電話番号（任意）
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="09012345678"
                        maxLength={11}
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "")
                          field.onChange(value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2.5 items-end">
                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="font-semibold text-slate-600">郵便番号</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="1234567"
                          maxLength={7}
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "")
                            field.onChange(value)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddressSearch}
                  loading={isSearchingAddress}
                >
                  住所検索
                </Button>
              </div>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-600">住所</FormLabel>
                    <FormControl>
                      <Input placeholder="東京都新宿区西新宿2-8-1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-2.5 justify-end">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
              キャンセル
            </Button>
            <Button type="submit" loading={isPending}>
              保存する
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
